import cx from 'classnames';
import type * as React from 'react';
import { useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import type { CaptionProps } from '@dx-ui/osc-caption';
import { Icon } from '@dx-ui/osc-icon';
import type { TFunction } from 'i18next';
import { CarouselImage } from './carousel-image';
import { CarouselNoImage } from './carousel-no-image';
import { useRect } from '@dx-ui/utilities-use-rect';
import { isRtl as isRtlLanguage } from '@dx-ui/utilities-get-language-direction';
import { useRouter } from 'next/router';
import { ANALYTICS_NO_VALUE, type BaseImageMetrics } from '@dx-ui/config-metrics';
import {
  dispatchCarouselInteraction,
  type CarouselImageClickPayload,
  type CarouselNavigationPayload,
} from './utils/carousel-analytics';
import { SkeletonLoader } from '@dx-ui/osc-skeleton-loader';
import { useForkedRef } from '@dx-ui/utilities-use-forked-ref';

export type CarouselImages = {
  isLCP?: boolean;
  alt: string;
  url: string;
  captionData?: CaptionProps;
  children?: React.ReactNode;
};

export type CarouselSinglePropsBase = {
  images?: CarouselImages[];
  startingIndex?: number;
  isLoading?: boolean;
  onArrowClick?: (index: number) => void;
  showAlternateControls?: boolean;
  isTailored?: boolean;
  metrics?: Partial<BaseImageMetrics>;
  /** Arrow/swipe navigation analytics callback (used only when legacy analytics is disabled). */
  onCarouselNavigation?: (payload: CarouselNavigationPayload) => void;
  /** Caption/image click analytics callback (used only when legacy analytics is disabled). */
  onCarouselImageClick?: (payload: CarouselImageClickPayload) => void;
  /** Legacy analytics gate. Defaults true to preserve current trackEvent behavior. */
  isLegacyCarouselAnalyticsEnabled?: boolean;
  ref?: React.Ref<HTMLDivElement>;
};

export type CarouselSingleProps = CarouselSinglePropsBase & React.HTMLAttributes<HTMLDivElement>;

const CarouselNavigationButton = ({
  children,
  direction,
  isTailored,
  ...rest
}: {
  children: React.ReactNode;
  direction: 'left' | 'right';
  isTailored?: CarouselSinglePropsBase['isTailored'];
} & React.HTMLAttributes<HTMLButtonElement>) => (
  <button
    className={cx('relative flex appearance-none items-center overflow-hidden', {
      '@[256px]/carousel:px-2 py-2 px-1 osc-carousel-button': !isTailored,
      'bg-surface p-2 md:p-3': isTailored,
    })}
    type="button"
    {...rest}
  >
    {children}
    <Icon
      name="arrowhead-left"
      className={cx('h-3 w-5 @[256px]/carousel:size-7 @[448px]/carousel:size-8', {
        '-scale-x-100': direction === 'right',
        'fill-surface-interactive': isTailored,
      })}
    />
  </button>
);

const CarouselControls = ({
  showAlternateControls,
  activeIndex,
  count,
  isRtl = false,
  isTailored = false,
  handlePreviousButtonClick,
  handleNextButtonClick,
  t,
}: {
  isRtl: boolean;
  isTailored?: CarouselSinglePropsBase['isTailored'];
  showAlternateControls?: boolean;
  count: number;
  activeIndex: number;
  handlePreviousButtonClick: () => void;
  handleNextButtonClick: () => void;
  t: TFunction<'osc-carousel'>;
}) => {
  const leftNav = (
    <CarouselNavigationButton
      data-testid="previousCarouselImage"
      direction={isRtl ? 'right' : 'left'}
      onClick={handlePreviousButtonClick}
      isTailored={isTailored}
    >
      <span className="sr-only">{t('previousImage')}</span>
    </CarouselNavigationButton>
  );

  const rightNav = (
    <CarouselNavigationButton
      data-testid="nextCarouselImage"
      direction={isRtl ? 'left' : 'right'}
      onClick={handleNextButtonClick}
      isTailored={isTailored}
    >
      <span className="sr-only">{t('nextImage')}</span>
    </CarouselNavigationButton>
  );

  const paginationCounter = showAlternateControls ? (
    <>
      <p
        aria-hidden
        className={cx('flex select-none items-center overflow-hidden', {
          'bg-surface p-2.5 md:p-4 tracking-wide font-semibold': isTailored,
          'px-1': !isTailored,
        })}
      >
        {activeIndex + 1} / {count}
      </p>
      <span className="sr-only" aria-live="polite">
        {t('imageCount', { activeIndex: activeIndex + 1, imageCount: count })}
      </span>
    </>
  ) : (
    <>
      <p
        data-osc-product="carousel-label"
        aria-hidden
        className="osc-carousel-label text--md absolute bottom-0 end-2 flex select-none items-center overflow-hidden px-2 py-1 opacity-90"
      >
        {activeIndex + 1} / {count}
      </p>
      <span className="sr-only" aria-live="polite">
        {t('imageCount', { activeIndex: activeIndex + 1, imageCount: count })}
      </span>
    </>
  );

  return showAlternateControls ? (
    <div
      data-testid="testAlternateControlClassName"
      className={cx('absolute flex', {
        'osc-carousel-label bottom-0 end-10 items-center': !isTailored,
        'bottom-1 end-1 md:bottom-4 md:end-4 gap-0.5 justify-evenly items-stretch': isTailored,
      })}
    >
      {isTailored ? (
        <>
          {leftNav}
          {rightNav}
          {paginationCounter}
        </>
      ) : (
        <>
          {leftNav}
          {paginationCounter}
          {rightNav}
        </>
      )}
    </div>
  ) : (
    <>
      {paginationCounter}
      <div className="absolute inset-y-1/2 flex w-full items-center justify-between">
        {leftNav}
        {rightNav}
      </div>
    </>
  );
};

/**
 *
 * A carousel that displays a single image at a time. Includes a built in loading (shimmer) and error handling state
 * @returns JSX.element
 */
export const CarouselSingle = ({
  className,
  images,
  startingIndex = 0,
  onArrowClick,
  isLoading = false,
  showAlternateControls,
  isTailored = false,
  metrics,
  onCarouselNavigation,
  onCarouselImageClick,
  isLegacyCarouselAnalyticsEnabled = true,
  ref: forwardedRef,
  ...rest
}: CarouselSingleProps) => {
  const { t } = useTranslation('osc-carousel');
  const { locale = 'en' } = useRouter();
  const isRtl = isRtlLanguage(locale);
  const [activeIndex, setActiveIndex] = useState(startingIndex);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const carouselContainerRef = useRef<HTMLDivElement | null>(null);
  const ref = useForkedRef(forwardedRef, carouselContainerRef);

  const count = images?.length ?? 0;

  const rect = useRect({ ref: count > 0 ? carouselContainerRef : { current: null } });

  const width = rect?.width ?? 0;
  const height = rect?.height ?? 0;
  const imageTranslateX = `${!isRtl ? '-' : ''}${
    activeIndex === 0 ? 0 : 100 / (count / activeIndex)
  }%`;

  const handleNextButtonClick = () => {
    const newIndex = images && activeIndex === images.length - 1 ? 0 : activeIndex + 1;
    if (onArrowClick) onArrowClick(newIndex);
    setActiveIndex(newIndex);

    dispatchCarouselInteraction({
      interaction: 'navigation',
      metricsParams: {
        metrics,
        images,
        itemTitle:
          images?.[activeIndex]?.captionData?.caption ||
          images?.[activeIndex]?.alt ||
          ANALYTICS_NO_VALUE,
        position: activeIndex + 1, // 1-based index of image visible when navigation fires
      },
      onInteraction: onCarouselNavigation,
      isLegacyCarouselAnalyticsEnabled,
    });
  };
  const handlePreviousButtonClick = () => {
    const newIndex = images && activeIndex === 0 ? images.length - 1 : activeIndex - 1;
    if (onArrowClick) onArrowClick(newIndex);
    setActiveIndex(newIndex);

    dispatchCarouselInteraction({
      interaction: 'navigation',
      metricsParams: {
        metrics,
        images,
        itemTitle:
          images?.[activeIndex]?.captionData?.caption ||
          images?.[activeIndex]?.alt ||
          ANALYTICS_NO_VALUE,
        position: activeIndex + 1, // 1-based index of image visible when navigation fires
      },
      onInteraction: onCarouselNavigation,
      isLegacyCarouselAnalyticsEnabled,
    });
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setTouchStart(e.targetTouches[0]?.clientX ?? 0);
  };
  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEnd(e.targetTouches[0]?.clientX ?? 0);
  };
  const handleTouchEnd = () => {
    //swipe sensitivity can be adjusted - travel distance in [number] pixels of user swipe event
    const SWIPE_SENSITIVITY = 50;
    //swipe left
    if (count > 1 && touchStart - touchEnd > SWIPE_SENSITIVITY && touchEnd > 0)
      handleNextButtonClick();
    //swipe right
    if (count > 1 && touchStart - touchEnd < -SWIPE_SENSITIVITY && touchEnd > 0)
      handlePreviousButtonClick();

    setTouchStart(0);
    setTouchEnd(0);
  };

  /** If content of gallery is loading present shimmer state to prevent CLS */
  if (isLoading)
    return (
      <SkeletonLoader
        className={cx('relative aspect-[3/2] w-full overflow-hidden', className)}
        data-testid="singleImageCarouselLoading"
        ref={ref}
        {...rest}
      />
    );

  /** No images are available return placeholder with message */
  if (!images || !count) {
    return <CarouselNoImage className={className} />;
  }

  return (
    <div
      className={cx('relative aspect-[3/2] w-full overflow-hidden @container/carousel', className)}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
      ref={ref}
      {...rest}
    >
      <div
        className="relative flex select-none overflow-hidden transition duration-500 ease-out-quint"
        data-testid="singleImageCarousel"
        style={{
          height,
          width: `${count * 100}%`,
          transform: `translate3d(${imageTranslateX}, 0, 0)`,
        }}
      >
        {images?.map((imageProps, index) => (
          <div
            className="relative size-full overflow-hidden bg-surface-legacy"
            key={`carousel-image-${imageProps.url ?? ''}-${imageProps.alt}`}
            style={{ width }}
            data-testid={`image-carousel-wrap-${index + 1}`}
          >
            <CarouselImage
              isActive={index === activeIndex}
              {...imageProps}
              imageProps={{
                priority: index === 0,
                fetchPriority: index === 0 && imageProps.isLCP ? 'high' : undefined,
              }}
              onCaptionClick={() =>
                dispatchCarouselInteraction({
                  interaction: 'imageClick',
                  metricsParams: {
                    metrics,
                    position: index + 1, // 1-based index
                    images,
                    itemTitle: imageProps?.captionData?.caption || ANALYTICS_NO_VALUE,
                  },
                  onInteraction: onCarouselImageClick,
                  isLegacyCarouselAnalyticsEnabled,
                })
              }
            />
            {imageProps.children ? <div>{imageProps.children}</div> : null}
          </div>
        ))}
      </div>
      {/* Only show carousel controls/count if we have more than 1 image passed in */}
      {count > 1 ? (
        <CarouselControls
          handleNextButtonClick={handleNextButtonClick}
          handlePreviousButtonClick={handlePreviousButtonClick}
          count={count}
          t={t}
          activeIndex={activeIndex}
          showAlternateControls={showAlternateControls}
          isTailored={isTailored}
          isRtl={isRtl}
        />
      ) : null}
    </div>
  );
};

export default CarouselSingle;
